import { closeNativeGameWindow, isNativeGameWindowClosing } from './nativeGameWindowClose.lifecycle';

// Browser-handle leases are local resources, not a second game/session owner.
// A reentrant callback can reclaim the same WindowProxy for a newer operation.
const popupLeases = new WeakMap();
// One named popup may await a close ACK. New browser opens are held until
// that resource is closed or superseded; no timer or unbounded retired list.
let retiredLease = null;
const reapRetiredLease = () => {
  const ticket = retiredLease;
  if (!ticket) return true;
  if (popupLeases.get(ticket.popup) !== ticket) {
    if (retiredLease === ticket) retiredLease = null;
  } else if (ticket.popup.closed) {
    if (popupLeases.get(ticket.popup) === ticket) popupLeases.delete(ticket.popup);
    if (retiredLease === ticket) retiredLease = null;
  }
  return !retiredLease;
};

// Same-URL browser handoff only; never obtains another provider launch URL.
export function createMobilePopout({ windowObject = window, setExternal, onClosed = () => {}, canOpen = () => false, manualClose = false }) {
  let operation = null;
  let revision = 0;
  let disposed = false;
  const owns = ticket => Boolean(ticket && operation === ticket && revision === ticket.revision &&
    (!ticket.popup || popupLeases.get(ticket.popup) === ticket));
  const authorized = ticket => owns(ticket) && canOpen(ticket.url, ticket.expectedLaunch) && owns(ticket);
  const closeDetached = (ticket, popup) => {
    if (!popup) return 'closed';
    if (popupLeases.has(popup) && popupLeases.get(popup) !== ticket) return 'superseded';
    if (retiredLease === ticket && ticket.popup === popup) {
      if (!reapRetiredLease()) return ticket.closeResult;
      return 'closed';
    }
    if (popupLeases.get(popup) === ticket) popupLeases.delete(popup);
    let result = 'pending';
    try {
      if (closeNativeGameWindow(popup, { manual: manualClose && ticket.external }) === 'failed') result = 'failed';
      if (popup.closed) return 'closed';
    } catch { result = 'failed'; }
    if (!popupLeases.has(popup)) {
      ticket.popup = popup; // Includes a revoked blank shell before adoption.
      ticket.closeResult = result;
      popupLeases.set(popup, ticket);
      retiredLease = ticket;
    }
    return result;
  };
  const release = (ticket, restoreInline = false) => {
    const valid = authorized(ticket);
    const restore = restoreInline && ticket.external && valid;
    const current = owns(ticket);
    if (current) { operation = null; revision += 1; }
    const releasedAt = revision;
    const closed = closeDetached(ticket, ticket.popup);
    if (closed !== 'closed' && current && valid && !operation && revision === releasedAt &&
      popupLeases.get(ticket.popup) === ticket) {
      ticket.revision = revision;
      ticket.restoreAfterClose = restore;
      operation = ticket; // Failed explicit close must not create a second frame.
      if (retiredLease === ticket) retiredLease = null;
    }
    // Closing a native window can itself start another operation. Neither a
    // stale failure nor an explicit return may remount that successor's frame.
    if (current && closed === 'closed' && restore && !operation && revision === releasedAt &&
      canOpen(ticket.url, ticket.expectedLaunch) && !operation && revision === releasedAt) {
      setExternal(false);
      return true;
    }
    // The existing UI's boolean means accepted, not frame already remounted.
    // A pending native close is not a popup-block error; only ACK restores UI.
    return Boolean(restore && closed === 'pending' && authorized(ticket));
  };
  const close = () => {
    const ticket = operation;
    operation = null;
    revision += 1; // Also fence an in-progress native open with no window yet.
    if (ticket) closeDetached(ticket, ticket.popup);
  };
  const requestManualClose = ({ restoreInline = false } = {}) => {
    const ticket = operation;
    if (!manualClose || !ticket?.external || !authorized(ticket)) return false;
    ticket.restoreAfterClose = restoreInline;
    return true;
  };
  return {
    hasResource: () => Boolean(operation?.popup && !operation.popup.closed),
    isActive: () => Boolean(operation?.popup && !operation.popup.closed && authorized(operation)),
    open(url, features = 'popup=yes,resizable=yes,scrollbars=yes', expectedLaunch) {
      // The consumer compares immutable launch identity fields, not reference
      // equality. Snapshot them before any browser/React callback can mutate.
      const expected = expectedLaunch && { ...expectedLaunch };
      if (disposed || !canOpen(url, expected) || !reapRetiredLease() || isNativeGameWindowClosing()) return false;
      if (operation?.popup && !operation.popup.closed && authorized(operation)) {
        const existing = operation;
        try { existing.popup.focus?.(); } catch { return false; }
        return authorized(existing);
      }
      if (operation) {
        const closingRevision = revision;
        close();
        if (operation || revision !== closingRevision + 1) return false;
      }
      if (disposed || !canOpen(url, expected) || !reapRetiredLease() || isNativeGameWindowClosing()) return false;
      const ticket = { revision: ++revision, url, expectedLaunch: expected, popup: null, external: false };
      operation = ticket;
      let next;
      try { next = windowObject.open('about:blank', 'crypto-mobile-game', features); }
      catch { release(ticket); return false; }
      if (!next || !authorized(ticket) || next.closed ||
        (popupLeases.has(next) && popupLeases.get(next) !== ticket)) {
        closeDetached(ticket, next);
        release(ticket);
        return false;
      }
      ticket.popup = next;
      popupLeases.set(next, ticket);
      try {
        next.opener = null;
        if (!authorized(ticket)) { release(ticket); return false; }
        // The caller synchronously removes the old frame before navigating.
        ticket.external = true;
        setExternal(true);
        if (!authorized(ticket)) { release(ticket); return false; }
        next.location.replace(url);
        if (!authorized(ticket)) { release(ticket); return false; }
        return true;
      } catch {
        release(ticket, true);
        return false;
      }
    },
    returnInline() {
      if (manualClose) return requestManualClose({ restoreInline: true });
      const ticket = operation;
      if (!ticket || !authorized(ticket)) return false;
      return release(ticket, true);
    },
    reconcile() {
      reapRetiredLease();
      const ticket = operation;
      if (!ticket?.popup?.closed) return;
      const notify = authorized(ticket);
      const current = owns(ticket);
      if (!current) return;
      operation = null;
      const releasedAt = ++revision;
      if (popupLeases.get(ticket.popup) === ticket) popupLeases.delete(ticket.popup);
      if (notify && canOpen(ticket.url, ticket.expectedLaunch) && !operation && revision === releasedAt) {
        if (ticket.restoreAfterClose) setExternal(false);
        else onClosed();
      }
    },
    requestManualClose,
    close,
    cleanup() { disposed = true; close(); },
  };
}

let presentation = null;
export function bindGamePopupPresentation(options) {
  const controller = createMobilePopout(options);
  presentation = controller;
  return { ...controller, cleanup() {
    controller.cleanup();
    if (presentation === controller) presentation = null;
  } };
}
export const openGamePopup = (url, features, expectedLaunch) => presentation?.open(url, features, expectedLaunch) || false;
export const returnGameInline = () => presentation?.returnInline() || false;
export const requestGamePopupManualClose = options => presentation?.requestManualClose(options) || false;
export const hasGamePopupResource = () => Boolean(presentation?.hasResource() || !reapRetiredLease());
export const isGamePopupActive = () => presentation?.isActive() || false;
export const reconcileGamePopup = () => presentation?.reconcile();
